import { useAnalytics } from '@/composables/useAnalytics'
import { ensureRouteData } from '@/lib/appLoad'
import { useAuthStore } from '@/stores/auth'
import {
  buildLoginQuery,
  handOffLoginRoute,
  resolvePostLoginRedirect,
  shouldHandOffToUnifiedLogin,
} from '@/utils/authLoginRedirect'
import { createRouter, createWebHistory, type RouteLocationRaw } from 'vue-router'
import { routes } from 'vue-router/auto-routes'

// Legacy Laravel CA URLs that Phoenix customers may still bookmark or arrive via email links.
// Browser /customer/membership/refer → router path /membership/refer → canonical Phoenix route.
// this is needed because when a Phoenix customer clicks on a bookmarked or marketing link such as lyka.com.au/membership/refer
// we first take them to the unified login with the correct redirect param which is .au/login?/redirect-/membership/refer
// the unified login will then log them in Phoenix, and prepend the redirect with a `/customer/` and then loads `/customer/membership/refer`
// that route doesn't exist in Phoenix, instead we have `/account/refer-a-friend, and exactly because of that, we need below mappings.
// These paths aren't file-based routes, so the beforeEach guard maps them to their Phoenix destination.
const legacyRedirects: Record<string, RouteLocationRaw> = {
  // Old membership login → unified login (deployed /login hands off automatically).
  '/membership/login': '/login',
  '/membership/dashboard': { name: 'deliveries' },
  '/account/dashboard': { name: 'deliveries' },
  '/membership/refer': { name: 'refer-a-friend' },
  '/account/plan': { name: 'dogs' },
  '/account/pupper': { name: 'dogs' },
  '/account/deliveries': { name: 'deliveries' },
  '/account/extras': { name: 'deliveries' },
  '/account/supplements': { name: 'deliveries' },
  '/account/profile': { name: 'account-home' },
}

export const resolveLegacyRedirect = (path: string): RouteLocationRaw | undefined => legacyRedirects[path]

const router = createRouter({
  history: createWebHistory(import.meta.env.VITE_BASE_URL),
  routes,
  scrollBehavior(to, _from, savedPosition) {
    if (savedPosition) {
      return savedPosition
    } else if (to.hash) {
      return {
        el: to.hash,
      }
    } else {
      return { top: 0 }
    }
  },
})

router.beforeEach(async (to) => {
  // Root path redirect lives here as vue-router 5's FBR codegen does not emit `redirect` from route blocks.
  if (to.path === '/') {
    return { name: 'deliveries' }
  }

  const legacyTarget = resolveLegacyRedirect(to.path)

  if (legacyTarget) {
    return legacyTarget
  }

  const authStore = useAuthStore()

  if (!authStore.isAuthenticated && !to.meta.unauthenticated) {
    // No session in memory and the destination requires one — try to recover from a refresh cookie before bouncing to login.
    await authStore.checkIfTokenNeedRefresh()
  } else {
    await authStore.checkIfTokenExpired()
  }

  if (authStore.isAuthenticated && to.path === '/login') {
    // Already signed in — honour ?redirect= (legacy or native) instead of always going to deliveries.
    const redirect = resolvePostLoginRedirect(to.query)

    return redirect ?? '/deliveries'
  }

  if (!authStore.isAuthenticated && !to.meta.unauthenticated) {
    return {
      path: '/login',
      query: buildLoginQuery(to.fullPath, to.query),
    }
  }

  if (to.path === '/login' && shouldHandOffToUnifiedLogin()) {
    handOffLoginRoute(to.fullPath, to.path)
    return false
  }

  if (authStore.isAuthenticated && !to.meta.unauthenticated) {
    await ensureRouteData(to.meta.requires ?? [])
  }
})

router.afterEach((to) => {
  if (to.name) {
    useAnalytics().page(String(to.name))
  }
})

export { router }
